import { startTransition, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import AsyncButton from '@wojtekmaj/react-async-button';
import T, { useTranslation } from '@wojtekmaj/react-t';

import { toast } from '@rewardopl/react-toast';

import Button from '@rewardopl/react-ui/button';
import CheckboxField from '@rewardopl/react-ui/checkbox_field';
import InputField from '@rewardopl/react-ui/input_field';

import { get, post } from '@rewardopl/utils/network';

import { ButtonWrapper, Form } from '../login/email/form.styles';

import PrivacyPolicyLink from '../../privacy_policy_link';
import ServiceContractLink from '../../service_contract_link';
import EmailTypoDialog from './email_typo_dialog';

import { correctEmailTypo } from '../../../utils/email';
import { isPasswordValid } from '../../../utils/password';

import { currentAccountRoleState, currentAccountState, currentBusinessState } from '../../../store';

import type { Account, AccountRole, Business } from '@rewardopl/types';

const pendingConfig = {
  disabled: true,
};

export default function RegisterForm() {
  const navigate = useNavigate();
  const setCurrentAccount = useSetAtom(currentAccountState);
  const setCurrentAccountRole = useSetAtom(currentAccountRoleState);
  const setCurrentBusiness = useSetAtom(currentBusinessState);
  const registerErrorString = useTranslation('Failed to register');
  const accountAlreadyExistsString = useTranslation('Account already exists');
  const passwordInvalidString = useTranslation(
    'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.',
  );

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isEmailTypoDialogOpen, setIsEmailTypoDialogOpen] = useState(false);
  const [originalEmail, setOriginalEmail] = useState('');
  const [suggestedEmail, setSuggestedEmail] = useState<string | null>(null);

  const action = '/api/businesses';

  function onBlur() {
    if (!email) {
      return;
    }

    const suggestedEmail = correctEmailTypo(email);

    if (suggestedEmail) {
      setOriginalEmail(email);
      setSuggestedEmail(suggestedEmail);
      setIsEmailTypoDialogOpen(true);
    }
  }

  function onClose() {
    setIsEmailTypoDialogOpen(false);
  }

  async function onClick(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    event.preventDefault();

    const submitButton = event.currentTarget;
    const form = submitButton.form;

    if (!form || !form.reportValidity()) {
      return;
    }

    const formData = new FormData(form);

    try {
      const business = (await post(action, {
        body: formData,
      })) as Business;

      const [account, accountRole] = await Promise.all([
        get('/api/accounts/current') as Promise<Account>,
        get('/api/account_roles/current') as Promise<AccountRole>,
      ]);

      startTransition(() => {
        setCurrentAccount(account);
        setCurrentAccountRole(accountRole);
        setCurrentBusiness(business);

        setTimeout(() => navigate('/onboarding'), 0);
      });
    } catch (err) {
      const error = err;

      const errorString = (() => {
        if (!(error instanceof Error)) {
          return registerErrorString;
        }

        switch (error.message) {
          case 'Password does not meet complexity requirements':
            return passwordInvalidString;
          case 'Account already exists':
            return accountAlreadyExistsString;
          default:
            return registerErrorString;
        }
      })();

      toast.error(errorString);

      if (error instanceof Error && error.message === 'Account already exists') {
        navigate('/login/email', { state: { email: formData.get('email') } });
      }
    }
  }

  return (
    <>
      <Form action={action} method="POST">
        <InputField
          autoComplete="username"
          label="Email"
          maxLength={320}
          name="email"
          onBlur={onBlur}
          onChange={setEmail}
          placeholder="email@example.com"
          type="email"
          value={email}
        />
        <InputField
          autoComplete="new-password"
          hint="Min. 8 characters, uppercase letter, lowercase letter, number, special character"
          label="Password"
          minLength={8}
          name="password"
          onChange={(nextPassword, event) => {
            setPassword(nextPassword);

            event.target.setCustomValidity(
              isPasswordValid(nextPassword) ? '' : passwordInvalidString,
            );
          }}
          placeholder="••••••••"
          type="password"
          value={password}
        />
        <CheckboxField
          label={
            <>
              <T>I have read and agree to the</T> <ServiceContractLink /> <T>and</T>{' '}
              <PrivacyPolicyLink />.
            </>
          }
          name="terms_accepted"
          required
        />
        <ButtonWrapper>
          <AsyncButton
            as={Button}
            className="primary"
            onClick={onClick}
            pendingConfig={pendingConfig}
            type="submit"
          >
            <T>Register</T>
          </AsyncButton>
        </ButtonWrapper>
      </Form>
      {suggestedEmail ? (
        <EmailTypoDialog
          isOpen={isEmailTypoDialogOpen}
          onClose={onClose}
          originalEmail={originalEmail}
          setEmail={setEmail}
          suggestedEmail={suggestedEmail}
        />
      ) : null}
    </>
  );
}
